import { useDispatch, useSelector } from 'react-redux';
import UserService from '../services/UserService';
import useAlerts from './useAlerts';
import {
    loadingUsers,
    deactivateStateUser,
    onSelectedUserPassword,
    loadingblockedUsers,
    blockStateUser,
    setPageSize,
    setCreateModalOpen,
    setUpdateModalOpen,
    updateUserField,
    resetUserForm,
    setSubmitting,
    setFormErrors,
    resetFormErrors,
    upsertUserInTable,
    prefillUserForm,
} from '../store/slices/users/usersSlice';
import type { RootState, AppDispatch } from '../store/store';
import { useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { initialUserForm, IUser, IUserForm, } from '../types/user/userInterfaces'
import useLoading from './useLoading';
import useApiErrorHandler from './useApiErrorHandler';

export const useUser = () => {
    const { show: showAlert } = useAlerts();
    const dispatch: AppDispatch = useDispatch();
    const { handle } = useApiErrorHandler();


    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
    const navigate = useNavigate();
    const {
        users,
        blockedUsers,
        userPasswordEdit,
        paginator,
        paginatorBlockedUsers,
        pageSize,
        isCreateModalOpen,
        isUpdateModalOpen,

        form,
        errors,
        isSubmitting,
    } = useSelector((state: RootState) => state.users);

    const handlerSearchByType = async ({ searchType, query = "", page = 0, size = pageSize }) => {
        try {
            showLoadingIndicator(); // Mostrar el modal de carga

            let result: AxiosResponse<any, any>;
            const trimmedQuery = query.trim();
            switch (searchType) {
                case 'all':
                    result = await UserService.findAllUsersWithPagination(page, size);
                    break;
                case 'query':
                    if (!trimmedQuery) {
                        showAlert("warning", "Entrada inválida", "Debe ingresar un término de búsqueda válido.");
                        hideLoadingIndicator();
                        return;
                    }
                    result = await UserService.searchUsersWithPagination(trimmedQuery, page, size);
                    break;
                default:
                    throw new Error(`Tipo de búsqueda no válido: ${searchType}`);
            }

            dispatch(loadingUsers(result.data));
        } catch (error) {
            handle(error)
        } finally {
            hideLoadingIndicator(); // Ocultar el modal de carga al finalizar la solicitud
        }
    };

    const updatePageSize = (size: number) => {
        dispatch(setPageSize(size));
    };

    const handleSearchUsersBlocking = async ({ searchType, query = "", page = 0, size = pageSize }) => {
        try {
            showLoadingIndicator(); // Mostrar el modal de carga

            let result: AxiosResponse<any, any>;
            const trimmedQuery = query.trim();
            switch (searchType) {
                case 'all':
                    result = await UserService.findBlockedUsers(page, size);
                    break;
                case 'query':
                    result = await UserService.searchLockedUsersWithPagination(trimmedQuery, page, size);
                    break;
                default:
                    throw new Error(`Tipo de búsqueda no válido: ${searchType}`);
            }

            dispatch(loadingblockedUsers(result.data));
        } catch (error) {
            handle(error);
        } finally {
            hideLoadingIndicator(); // Ocultar el modal de carga después de la solicitud
        }
    };


    const handlerDeactivateUser = async ({ id }) => {
        try {
            const response = await UserService.deleteUserById(id);
            if (response.data?.statusCode === 200) {
                dispatch(deactivateStateUser(response.data?.data));
                showAlert('success', response.data?.title, response.data?.message);
            } else {
                showAlert('error', 'Error', 'Se produjo un error al desactivar el usuario.');
            }
        } catch (error) {
            handle(error);
        }
    };

    const handleBlockingUsers = async ({ id }) => {
        try {
            const response = await UserService.lockUserOrUnLockUser(id);
            if (response.data?.statusCode === 200) {
                dispatch(blockStateUser(response.data?.data));
                showAlert('success', response.data?.title, response.data?.message);
            } else {
                showAlert('error', 'Error', 'Se produjo un error al desactivar la cuenta.');
            }
        } catch (error) {
            handle(error);
        }
    };

    /**
     * 🧩 handlerAddUser
     * ---------------------------------------------------------------
     * Controlador responsable de gestionar el flujo completo de registro
     * de usuarios desde el frontend:
     *
     * 🔹 Envía los datos del formulario al backend (UserService.saveUser)
     * 🔹 Muestra indicadores globales de carga
     * 🔹 Controla estado de envío (`isSubmitting`) a nivel Redux
     * 🔹 Maneja errores de forma centralizada
     * 🔹 Actualiza la tabla sin consultar nuevamente al servidor
     * 🔹 Limpia formulario y errores al finalizar
     *
     * @async
     * @param user - Objeto de formulario tipado con IUserForm,
     *               contiene los datos necesarios para crear un usuario.
     *
     * 📌 Este método NO actúa directamente sobre componentes UI,
     *     delega mensajes al hook `useAlerts` y estados globales Redux.
     *
     * ✅ Beneficios:
     *   - Mantiene UI reactiva y sin bloqueos
     *   - Proporciona feedback visual inmediato al usuario
     *   - No recarga la tabla con nuevas peticiones
     *   - Previene submissions duplicados
     */
    const handlerAddUser = async (user: IUserForm) => {
        // console.log(user);

        try {
            // ⏳ Mostrar indicador de carga global
            showLoadingIndicator();

            // 🚫 Evitar submissions múltiples
            dispatch(setSubmitting(true));

            // 📨 Enviar formulario al backend
            const response = await UserService.saveUser(user);
            // console.log(response);

            const { statusCode, type, title, message, data } = response;

            // ✅ Creación exitosa
            if (statusCode === 201) {
                // 🎉 Notificación al usuario
                showAlert(type, title, message);

                // 🧹 Limpiar formulario y errores de validación
                resetForm();
                clearErrors();

                // 🚪 Cerrar modal de registro
                closeCreateUserModal();

                // 🔁 Actualizar tabla en memoria (sin recargar)
                addOrUpdateUserToTable(data);
            } else {
                // ❌ Manejo de fallos controlados
                showAlert('error', 'Error', 'No se pudo guardar el usuario');
            }

        } catch (error) {
            // ⚠️ Delegar errores al manejador central
            handle(error);

        } finally {
            // ✅ Permitir nuevas solicitudes
            dispatch(setSubmitting(false));

            // 🧭 Ocultar indicador de carga global
            hideLoadingIndicator();
        }
    };

    /**
     * 🛠️ handleUpdateUser
     * -----------------------------------------------------------------
     * Controlador central del flujo de actualización de usuarios.
     *
     * Se encarga de:
     *  🔹 Activar indicador global de carga
     *  🔹 Bloquear dobles envíos con `isSubmitting`
     *  🔹 Ejecutar petición al backend (`UserService.updateUserById`)
     *  🔹 Actualizar el usuario en la tabla sin recargar
     *  🔹 Limpiar formulario y errores al finalizar
     *  🔹 Cerrar modal con estado limpio
     *
     * @async
     * @returns {Promise<void>}
     *
     * ✅ Beneficios:
     *  - Mantiene UI reactiva y consistente
     *  - Evita consultas extra al backend (optimiza tabla local)
     *  - Manejo escalable y centralizado
     */
    const handleUpdateUser = async (user: IUserForm) => {
        // console.log(user);
        try {
            // ⏳ Mostrar indicador de carga global (UX)
            showLoadingIndicator();

            // 🚫 Bloquear múltiples submissions
            dispatch(setSubmitting(true));

            // 📨 Enviar actualización al backend
            const response = await UserService.updateUser(user.id, user);

            const { statusCode, type, title, message, data } = response;

            // ✅ Respuesta exitosa
            if (statusCode === 200) {
                // 💬 Notificación visual
                showAlert(type, title, message);

                // 🔁 Agregar o actualizar usuario en tabla local
                addOrUpdateUserToTable(data);

                // 🧹 Limpiar estado
                resetForm();
                clearErrors();

                // 🚪 Cerrar modal de edición
                closeUpdateUserModal();
            } else {
                // ❌ Error controlado
                showAlert('error', 'Error', 'No se pudo actualizar el usuario');
            }

        } catch (error) {
            // ⚠️ Manejo central de errores
            handle(error);

        } finally {
            // ✅ Habilitar nuevo envío
            dispatch(setSubmitting(false));

            // 🧭 Ocultar spinner global
            hideLoadingIndicator();
        }
    };

    /**
     * 🔍 Obtiene la información de un usuario por su ID y
     * precarga el formulario de edición con los datos recibidos.
     *
     * ▶️ Flujo:
     *  - Muestra un indicador de carga
     *  - Consulta el backend (`GET /users/{id}`)
     *  - Prellena el formulario centralizado en Redux
     *  - Abre el modal de actualización
     *  - Muestra una notificación con el resultado
     *
     * ✅ Ideal para:
     *  - Botones "Editar" en tablas paginadas
     *  - Buscar información actualizada del usuario
     *
     * ❗Maneja correctamente:
     *  - Errores backend
     *  - Reset visual
     *  - Mensajes UX
     *
     * @param id - Identificador único del usuario a editar.
     */
    const fetchUserToEdit = async (id: number) => {
        try {
            showLoadingIndicator(); // ⏳ UI Feedback mientras carga

            const response = await UserService.getUserById(id);
            const { statusCode, type, title, message, data } = response;

            if (statusCode === 200) {
                dispatch(prefillUserForm(data));
                openUpdateUserModal();
                showAlert(type, title, message);
            } else {
                showAlert("error", "Error", "No se pudo obtener el usuario");
            }

        } catch (error) {
            handle(error); // 🚨 Manejo centralizado de excepciones
        } finally {
            hideLoadingIndicator(); // ✅ La UI siempre se limpia
        }
    };



    const handleUpdatePassword = async (data) => {
        try {
            const response = await UserService.updatePassword(data.id, data);
            const { title, message } = response.data;
            showAlert('success', title, message);
            navigate('/security/users/page');
        } catch (error) {
            handle(error);
        }
    };

    const handleOnSelectedUserToUpdatePassword = (user) => {
        dispatch(onSelectedUserPassword({ ...user }));
        navigate('/security/users/update-password');
    };

    // 🟢 Crear usuario
    const openCreateUserModal = () => dispatch(setCreateModalOpen(true));
    const closeCreateUserModal = () => dispatch(setCreateModalOpen(false));

    // 🟣 Actualizar usuario
    const openUpdateUserModal = () => dispatch(setUpdateModalOpen(true));
    const closeUpdateUserModal = () => dispatch(setUpdateModalOpen(false));

    /**
     * ❌ Cierra el modal de creación de usuario
     * y limpia el formulario del store.
     *
     * 👉 Se usa cuando:
     *  - El usuario cancela la creación
     *  - Se completó exitosamente el registro
     */
    const handleCloseCreate = () => {
        dispatch(setCreateModalOpen(false));
        resetForm();
        clearErrors();
    };

    /**
     * ❌ Cierra el modal de actualización de usuario
     * y limpia el formulario del store.
     *
     * 👉 Se usa cuando:
     *  - El usuario cancela la edición
     *  - Se completó la actualización
     */
    const handleCloseUpdate = () => {
        dispatch(setUpdateModalOpen(false));
        resetForm();
        clearErrors();
    };

    /**
     * ✏️ Actualiza un campo específico dentro del formulario de usuario.
     *
     * @template K - Nombre del campo (keyof IUserForm)
     * @param field - Clave del formulario que se modificará
     * @param value - Nuevo valor para dicho campo
     *
     * ✅ Ideal para inputs controlados
     * ✅ 100% type-safe con IntelliSense
     */
    const updateField = <K extends keyof typeof initialUserForm>(
        field: K,
        value: typeof initialUserForm[K]
    ) => {
        dispatch(updateUserField({ field, value }));
    };

    /**
     * 🧹 Reinicia el formulario de usuario al estado inicial definido.
     *
     * 👉 Se usa cuando:
     *  - Se cierra un modal
     *  - Comienza un registro nuevo
     *  - Se completa una acción exitosamente
     */
    const resetForm = () => {
        dispatch(resetUserForm());
    };

    /** ❌ Asigna errores */
    const setErrors = (errs: Record<string, string>) =>
        dispatch(setFormErrors(errs));

    /** ✅ Limpia errores */
    const clearErrors = () => dispatch(resetFormErrors());

    /**
     * 🔁 addOrUpdateUserToTable
     * -----------------------------------------------------------------
     * Actualiza eficientemente el listado de usuarios renderizado en
     * la tabla de frontend, sin necesidad de volver a consultar el backend.
     *
     * Ideal después de:
     *  ✅ Crear un usuario
     *  ✅ Actualizar un usuario existente
     *
     * Flujo interno:
     *  - Si el usuario existe en la tabla, lo actualiza (merge de propiedades)
     *  - Si no existe, lo agrega al listado
     *
     * Beneficios:
     *  🔹 Evita solicitudes extras al servidor (optimiza performance)
     *  🔹 Mantiene la interfaz reactiva
     *  🔹 Actualiza el UI al instante
     *
     * @param {IUser} user - Objeto usuario recién creado o actualizado,
     *                      retornado por el backend.
     *
     * @example
     * // Después de guardar o editar:
     * addOrUpdateUserToTable(data);
     *
     */
    const addOrUpdateUserToTable = (user: IUser) => {
        dispatch(upsertUserInTable(user));
    };


    return {
        // Estados
        users,
        userPasswordEdit,
        paginator,
        blockedUsers,
        paginatorBlockedUsers,
        // Funciones
        handlerSearchByType,
        handlerDeactivateUser,

        handlerAddUser,
        handleUpdateUser,
        fetchUserToEdit,

        handleUpdatePassword,
        handleOnSelectedUserToUpdatePassword,
        handleSearchUsersBlocking,
        handleBlockingUsers,
        // Pagina
        pageSize,
        updatePageSize,

        isCreateModalOpen,
        isUpdateModalOpen,
        openCreateUserModal,
        closeCreateUserModal,
        openUpdateUserModal,
        closeUpdateUserModal,

        handleCloseCreate,
        handleCloseUpdate,

        form,
        errors,
        isSubmitting,
        updateField,
        resetForm,
        setErrors,
        clearErrors,
        addOrUpdateUserToTable,
    };
};
